import React, { useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Tooltip,
    Typography,
    Box,
    Chip,
    useTheme,
    TableSortLabel,
    useMediaQuery
} from "@mui/material";
import { Delete, VpnKey } from "@mui/icons-material";
import { RoleWithStats } from "../../../../../models/DB";
import { formatTimestamp } from "../../../../../utils/customFunctions";
import { PermissionWrapper } from "../../../../../components/PermissionWrapper";
import { RoleManagementService } from "../../../../../services/supabase/RoleManagementService";
import { PermissionEditor } from "./PermissionEditor";
import toastService from "../../../../../services/toast/ToastService";
import { TableControlBar } from "../../../../../components/TableControlBar";
import { PortalTable } from "../../../../../components/PortalTable";
import { tablePreferencesStore } from "../../../../../app/stores/TablePreferences";

interface RolesTableProps {
    roles: RoleWithStats[];
}

export const RolesTable: React.FC<RolesTableProps> = ({ roles }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    // Get preferences from store
    const { 
        roles: rolesPrefs, 
        updateRolesPreferences 
    } = tablePreferencesStore();
    
    const [expandedRole, setExpandedRole] = useState<string | null>(null);
    const [editingRole, setEditingRole] = useState<string | null>(null);
    const [order, setOrder] = useState<'asc' | 'desc'>('asc');
    const [orderBy, setOrderBy] = useState<keyof RoleWithStats>('roleName');
    const [dense, setDense] = useState(rolesPrefs.dense);
    const [refreshLoading, setRefreshLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const handleDeleteRole = async (role: RoleWithStats) => {
        if (role.isSystem) {
            toastService.showError('System roles cannot be deleted.');
            return;
        }
        if (role.userCount > 0) {
            toastService.showError(`Cannot delete role. It is assigned to ${role.userCount} user(s).`);
            return;
        }

        const success = await RoleManagementService().deleteRole(role.roleId);
        if (success) {
            toastService.showSuccess(`Role "${role.roleName}" has been deleted.`);
        }
    };

    const handleEditPermissions = (role: RoleWithStats) => {
        setExpandedRole(expandedRole === role.roleId ? null : role.roleId);
        setEditingRole(null); // Reset editing state when toggling expand
    };

    const handleRequestSort = (_event: React.MouseEvent<unknown>, property: keyof RoleWithStats) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleRefresh = async () => {
        setRefreshLoading(true);
        await RoleManagementService().getAllRoles();
        setRefreshLoading(false);
    };

    // Filter roles by search query
    const filteredRoles = roles?.filter(role => {
        if (!searchQuery.trim()) return true;
        const query = searchQuery.toLowerCase();
        return (
            role.roleName?.toLowerCase().includes(query) ||
            role.description?.toLowerCase().includes(query)
        );
    }) || [];

    const sortedRoles = filteredRoles.slice().sort((a, b) => {
        if (a[orderBy] === null && b[orderBy] === null) {
            return 0;
        } else if (a[orderBy] === null) {
            return 1;
        } else if (b[orderBy] === null) {
            return -1;
        }

        if (typeof a[orderBy] === 'string' && typeof b[orderBy] === 'string') {
            return (
                order === 'desc' ?
                    (a[orderBy] as string).localeCompare(b[orderBy] as string)
                    :
                    (b[orderBy] as string).localeCompare(a[orderBy] as string)
            );
        } else {
            return (
                order === 'asc' ?
                    (a[orderBy] as number) - (b[orderBy] as number)
                    :
                    (b[orderBy] as number) - (a[orderBy] as number)
            );
        }
    });

    return (
        <Box
            sx={{
                width: '100%',
                mb: 4,
            }}
        >
            {/* Controls Bar */}
            <TableControlBar
                dense={dense}
                onDenseChange={(newDense) => {
                    setDense(newDense);
                    updateRolesPreferences({ dense: newDense });
                }}
                isLoading={refreshLoading}
                onRefresh={handleRefresh}
                search={{
                    value: searchQuery,
                    onChange: setSearchQuery,
                    placeholder: 'Search roles by name or description...'
                }}
                filterDescription={
                    searchQuery
                        ? `Showing ${filteredRoles.length} of ${roles?.length || 0} roles matching "${searchQuery}"`
                        : `Showing all ${roles?.length || 0} roles`
                }
            />

            <PortalTable>
                <TableContainer>
                    <Table stickyHeader size={dense ? 'small' : 'medium'}>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <TableSortLabel
                                        sx={{ fontFamily: 'primaryFont', fontWeight: 'bold', letterSpacing: 1 }}
                                        active={orderBy === 'roleName'}
                                        direction={orderBy === 'roleName' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'roleName')}
                                    >
                                        Role
                                    </TableSortLabel>
                                </TableCell>
                                {/* Description - hidden on mobile */}
                                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                    <Typography sx={{ fontFamily: 'primaryFont', fontWeight: 'bold', letterSpacing: 1 }}>
                                        Description
                                    </Typography>
                                </TableCell>
                                {/* Users - hidden on mobile */}
                                <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                    <TableSortLabel
                                        sx={{ fontFamily: 'primaryFont', fontWeight: 'bold', letterSpacing: 1 }}
                                        active={orderBy === 'userCount'}
                                        direction={orderBy === 'userCount' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'userCount')}
                                    >
                                        Users
                                    </TableSortLabel>
                                </TableCell>
                                {/* Permissions - hidden on mobile */}
                                <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                    <TableSortLabel
                                        sx={{ fontFamily: 'primaryFont', fontWeight: 'bold', letterSpacing: 1 }}
                                        active={orderBy === 'permissionCount'}
                                        direction={orderBy === 'permissionCount' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'permissionCount')}
                                    >
                                        Permissions
                                    </TableSortLabel>
                                </TableCell>
                                {/* Created - hidden on mobile */}
                                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                    <TableSortLabel
                                        sx={{ fontFamily: 'primaryFont', fontWeight: 'bold', letterSpacing: 1 }}
                                        active={orderBy === 'created'}
                                        direction={orderBy === 'created' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'created')}
                                    >
                                        Created
                                    </TableSortLabel>
                                </TableCell>
                                {/* Actions */}
                                <TableCell align="right">
                                    <Typography sx={{ fontFamily: 'primaryFont', fontWeight: 'bold', letterSpacing: 1 }}>
                                        Actions
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedRoles.map((role, index) => {
                                return (<React.Fragment key={index}>
                                    <TableRow
                                        hover
                                        sx={{
                                            backgroundColor: theme.palette.background.paper,
                                            '&:hover': {
                                                backgroundColor: theme.palette.mode === 'dark'
                                                    ? theme.palette.action.hover
                                                    : theme.palette.grey[50]
                                            }
                                        }}
                                    >
                                        {/* Role Name + Description on mobile */}
                                        <TableCell>
                                            <Box display="flex" alignItems="center">
                                                {role.isSystem && <Chip label="System" size="small" color="primary" sx={{ mr: 1, fontSize: isMobile ? 9 : 'inherit' }} />}
                                                <Box>
                                                    <Typography 
                                                        variant="body2" 
                                                        fontWeight="bold" 
                                                        sx={{ 
                                                            fontFamily: 'primaryFont', 
                                                            letterSpacing: 0.5,
                                                            fontSize: isMobile ? 12 : 14
                                                        }}
                                                    >
                                                        {role.roleName}
                                                    </Typography>
                                                    {/* Mobile: Show description and stats below name */}
                                                    {isMobile && (
                                                        <Box sx={{ mt: 0.5 }}>
                                                            {role.description && (
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: 10,
                                                                        fontFamily: 'primaryFont',
                                                                        color: theme.palette.text.secondary,
                                                                        maxWidth: 180,
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap'
                                                                    }}
                                                                >
                                                                    {role.description}
                                                                </Typography>
                                                            )}
                                                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: 9,
                                                                        fontFamily: 'primaryFont',
                                                                        color: theme.palette.text.secondary,
                                                                        backgroundColor: theme.palette.action.hover,
                                                                        px: 0.75,
                                                                        py: 0.25,
                                                                        borderRadius: 0.5
                                                                    }}
                                                                >
                                                                    {role.userCount} users
                                                                </Typography>
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: 9,
                                                                        fontFamily: 'primaryFont',
                                                                        color: theme.palette.text.secondary,
                                                                        backgroundColor: theme.palette.action.hover,
                                                                        px: 0.75,
                                                                        py: 0.25,
                                                                        borderRadius: 0.5
                                                                    }}
                                                                >
                                                                    {role.permissionCount} perms
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        {/* Description - hidden on mobile */}
                                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                            <Typography sx={{ fontSize: 12, fontFamily: 'primaryFont', letterSpacing: 0.5 }}>
                                                {role.description}
                                            </Typography>
                                        </TableCell>
                                        {/* Users - hidden on mobile */}
                                        <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                            <Typography sx={{ fontSize: 12, fontFamily: 'primaryFont', letterSpacing: 0.5 }}>
                                                {role.userCount}
                                            </Typography>
                                        </TableCell>
                                        {/* Permissions - hidden on mobile */}
                                        <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                            <Typography sx={{ fontSize: 12, fontFamily: 'primaryFont', letterSpacing: 0.5 }}>
                                                {role.permissionCount}
                                            </Typography>
                                        </TableCell>
                                        {/* Created - hidden on mobile */}
                                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                            <Typography sx={{ fontSize: 12, fontFamily: 'primaryFont', letterSpacing: 0.5 }}>
                                                {formatTimestamp(role.created)}
                                            </Typography>
                                        </TableCell>
                                        {/* Actions */}
                                        <TableCell align="right">
                                            {isMobile ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.75 }}>
                                                    <PermissionWrapper screen="roles" permission="update">
                                                        <IconButton 
                                                            size="small"
                                                            onClick={() => handleEditPermissions(role)}
                                                            sx={{ 
                                                                p: 1,
                                                                backgroundColor: theme.palette.action.hover,
                                                                '&:hover': {
                                                                    backgroundColor: theme.palette.action.selected
                                                                }
                                                            }}
                                                        >
                                                            <VpnKey sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    </PermissionWrapper>
                                                    <PermissionWrapper screen="roles" permission="delete">
                                                        <IconButton 
                                                            size="small"
                                                            onClick={() => handleDeleteRole(role)} 
                                                            disabled={role.isSystem || role.userCount > 0}
                                                            sx={{ 
                                                                p: 1,
                                                                backgroundColor: role.isSystem || role.userCount > 0 
                                                                    ? theme.palette.action.disabledBackground 
                                                                    : theme.palette.error.main + '15',
                                                                '&:hover': {
                                                                    backgroundColor: theme.palette.error.main + '30'
                                                                }
                                                            }}
                                                        >
                                                            <Delete sx={{ fontSize: 16 }} color={role.isSystem || role.userCount > 0 ? 'disabled' : 'error'} />
                                                        </IconButton>
                                                    </PermissionWrapper>
                                                </Box>
                                            ) : (
                                                <>
                                                    <PermissionWrapper screen="roles" permission="update">
                                                        <Tooltip title="Edit Permissions">
                                                            <IconButton onClick={() => handleEditPermissions(role)}>
                                                                <VpnKey />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                    <PermissionWrapper screen="roles" permission="delete">
                                                        <Tooltip title={role.isSystem ? 'System roles cannot be deleted' : role.userCount > 0 ? `Cannot delete role with ${role.userCount} user(s)` : 'Delete Role'}>
                                                            <span>
                                                                <IconButton onClick={() => handleDeleteRole(role)} disabled={role.isSystem || role.userCount > 0}>
                                                                    <Delete />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                </>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                    {expandedRole === role.roleId && (
                                        <PermissionEditor
                                            role={role}
                                            open={expandedRole === role.roleId}
                                            isEditing={editingRole === role.roleId}
                                            setIsEditing={(editing) => setEditingRole(editing ? role.roleId : null)}
                                        />
                                    )}
                                </React.Fragment>);
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </PortalTable>
        </Box>
    );
}; 